import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaRegEdit, FaRegTrashAlt, FaExclamationTriangle, FaLock } from 'react-icons/fa';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CreateUserModal from './CreateUserForm';
import PasswordChangeModal from './changeUserPassword';
import { usertableData, deleteUser } from './../../../../services/userMangement/userManagement';
import { lastLoginBodyTemplate } from '../../../../utility/helper';


const statusBodyTemplate = (rowData) => {
  return (
    <span className={`px-3 py-1 rounded-full text-white text-sm ${rowData.is_active ? 'bg-green-500' : 'bg-red-500'}`}>
      {rowData.is_active ? 'Enable' : 'Disable'}
    </span>
  );
};


const UserManagement = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [passWordModalVisible, setPassWordModalVisible] = useState(false);

  const [type, setType] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [limit, setLimit] = useState(10)
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const toast = useRef(null);

  const confirmDelete = (user) => {
    confirmDialog({
      message: 'Are you sure you want to delete this user?',
      header: 'Confirm Delete',
      icon: <FaExclamationTriangle />,
      style: { backgroundColor: '#0C153' },
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptClassName: 'bg-secondary hover:bg-gray-400 font-bold py-2 px-4 rounded ml-4 text-primary border focus:border-',
      rejectClassName: 'bg-primary hover:border-white text-gray-300 font-bold py-2 px-4 rounded border border-[rgb(161,147,147)]',
      accept: () => handleDelete(user),
    });
  };

  const handleDelete = async (user) => {
    try {
      const accessToken = JSON.parse(localStorage.getItem('access_token'));
      const response = await deleteUser(accessToken, user?.id);
      if (response) {
        await fetchUsers();
        toast.current.show({className:"bg-green-300 text-white", severity: 'success', summary: 'Success', detail: 'User deleted successfully', life: 3000 });
      }
    } catch (err) {
      toast.current.show({className:"bg-yellow-700 text-white", severity: 'warn', summary: 'Warning', detail: 'Failed to delete user', life: 3000 });
    }
  };
  

  const actionBodyTemplate = (rowData) => {
  return (
    <div className="flex gap-2">
      <button
        title="Edit User"
        onClick={() => {
          setModalVisible(true);
          setType("edit");
          setSelectedUser(rowData);
        }}
      >
        <FaRegEdit />
      </button>
      
      <button
        title="Change User Password"
        onClick={() => {
          setPassWordModalVisible(true);
          setType("edit");
          setSelectedUser(rowData);
        }}
      >
        <FaLock />
      </button>
      <button title="Delete User" onClick={() => confirmDelete(rowData)} className='text-red-500'>
        <FaRegTrashAlt />
      </button>
    </div>
  );
};


  useEffect(() => {
    fetchUsers();
  }, [searchQuery, limit, page]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = JSON.parse(localStorage.getItem('access_token'));
      const response = await usertableData(accessToken, searchQuery, limit, page);

      if (response.status === 200) {
        setUsers(response?.data);
      } else {
        throw new Error('Failed to fetch user data');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setPage(0);
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex items-center px-4 rounded gap-2 mt-2">
        <span className='text-2xl text-secondary'>User Management</span>
      </div>

      <div className="p-4">
        <h2 className="text-xl mb-4">Users List</h2>

        <div className='flex justify-between items-center mb-4'>
          <div className="flex items-center border border-gray-600 text-white px-4 py-1 rounded-md w-1/4">
            <FaSearch className="mr-2" />
            <form autoComplete="off">
              <input
              type="text"
              name="searchUserInput"
              placeholder="Search by name or email"
              className="bg-transparent outline-none w-full"
              value={searchQuery || ''}
              onChange={handleSearchChange}
              autoComplete="off"
              />
            </form>
          </div>
          <div>
            <button type='button'
              className="flex items-center py-[4px] px-4 rounded text-primary font-extrabold text-base hover:bg-gray-100 cursor-pointer bg-secondary hover:bg-blue-700"
              onClick={() => { setModalVisible(true); setType("create"); }}
            >
              + Create New
            </button>
          </div>
        </div>
        {error && <p className="text-red-500">{error}</p>}

        <DataTable 
        lazy
      value={users?.users} 
      stripedRows
      className='usertable'
      paginator
      rows={limit}
      totalRecords={users?.total}
      first={page * limit}
      onPage={(e) => {
        setPage(e.page);
        setLimit(e.rows);
      }}
      loading={loading}
      rowsPerPageOptions={[5, 10, 25, 50]}
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
    >
          <Column field="name" header="Full Name" />
          <Column field="email" header="Email Address" />
          <Column field="last_login_at" header="Last Logged In" body={lastLoginBodyTemplate} />
          <Column field="status" header="Status" body={statusBodyTemplate} />
          <Column header="Actions" body={actionBodyTemplate} />
        </DataTable>

        <CreateUserModal 
          visible={modalVisible} 
          fetchUsers={fetchUsers} 
          onHide={() => { setModalVisible(false); }} 
          type={type} 
          toast={toast} 
          selectedUser={selectedUser}
          loading={loading}
          setLoading={setLoading}
        />
        <PasswordChangeModal 
          visible={passWordModalVisible} 
          fetchUsers={fetchUsers} 
          onHide={() => { setPassWordModalVisible(false); }}
          type={type} 
          toast={toast} 
          selectedUser={selectedUser}
          loading={loading}
          setLoading={setLoading}
        />
      </div>
    </>
  );
};

export default UserManagement;
